import { Mail, Sparkles, Clock, AlertTriangle } from "lucide-react";
import type { Org } from "../../lib/apiTyped";
import { Card } from "./Card";
import { buildContactMailto } from "../../lib/contact";
import { millisFromTs, tierDisplayName } from "../../lib/format";

// Visual surface for everything that can be true about an org's plan
// status *outside* of a normal paid tier:
//   1) Trial active and still in window → muted "días restantes" pill.
//   2) Trial expired → loud "renew / contact" banner.
//   3) A paid-plan request is pending our approval → informational banner.
// All three are best-effort UI only. The hard enforcement lives in the
// preflight (`tierSlug==='trial' && Date.now() > trialExpiresAt`).

function daysBetween(fromMs: number, toMs: number): number {
  return Math.max(0, Math.ceil((toMs - fromMs) / (24 * 60 * 60 * 1000)));
}

export function TrialStatusBanner({
  org,
  orgId,
  email,
}: {
  org: Org | Record<string, unknown> | null;
  orgId: string;
  email?: string;
}) {
  if (!org) return null;
  const orgData = org as {
    name?: string;
    activeTier?: string | null;
    trial?: { expiresAt?: unknown } | null;
    pendingPlanRequest?: { tierSlug?: string } | null;
  };
  const orgName = orgData.name;
  const ctx = { orgId, orgName, email };

  const isTrial = orgData.activeTier === "trial";
  const expiresAtMs = millisFromTs(orgData.trial?.expiresAt);
  const now = Date.now();
  const expired = isTrial && expiresAtMs != null && now > expiresAtMs;
  const pending = orgData.pendingPlanRequest;

  if (!isTrial && !pending) return null;

  return (
    <div className="space-y-3">
      {isTrial && expired && (
        <Card
          className="p-4 sm:p-5 border-2"
          style={{ borderColor: "rgba(198,20,24,0.5)" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-error" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-ink">
                Tu prueba gratuita ha expirado
              </div>
              <p className="text-sm text-ink-2 mt-1">
                Tu organización ya no puede generar nuevos resultados. Escríbenos
                para activar un plan completo y seguir usando Formular.
              </p>
              <a
                href={buildContactMailto(
                  "Activar un plan tras la prueba",
                  ctx,
                )}
                className="inline-flex items-center gap-2 mt-3 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-80 transition-opacity"
              >
                <Mail className="w-3.5 h-3.5" />
                Contactar para activar
              </a>
            </div>
          </div>
        </Card>
      )}

      {isTrial && !expired && expiresAtMs != null && (
        <Card className="p-4 sm:p-5 bg-gradient-to-br from-ink/5 to-transparent">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 shrink-0 text-ink" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink">
                Prueba gratuita · {daysBetween(now, expiresAtMs)} días restantes
              </div>
              <p className="text-xs text-ink-3 mt-0.5">
                Al terminar, escríbenos para mantener tu acceso con un plan
                completo.
              </p>
            </div>
            <Clock className="w-4 h-4 shrink-0 text-ink-3" />
          </div>
        </Card>
      )}

      {pending?.tierSlug && (
        <Card className="p-4 sm:p-5 bg-gradient-to-br from-ink/5 to-transparent">
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 mt-0.5 shrink-0 text-ink" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink">
                Tu solicitud para el plan {tierDisplayName(pending.tierSlug)} está
                pendiente
              </div>
              <p className="text-xs text-ink-3 mt-0.5">
                Te contactamos por email para activarlo. Si no recibes
                respuesta en 24h, escríbenos.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
